import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// GameView.tsx
import { Component, createRef } from "react";
import { unityBridge } from "./UnityBridge";
import { aspectStore } from "./aspectStore";
const BUILD_URL = "Build";
// Unity/Emscripten registers input event targets by building the selector `'#' + canvas.id`, so the
// canvas MUST have a (matching) id or it throws "'#' is not a valid selector" and input dies.
const CANVAS_ID = "unity-canvas";
const LOADER_URL = `${BUILD_URL}/Conveyor.ContentEditor.loader.js`;
// Right-hand game preview: hosts the Unity WebGL canvas at a fixed 18:39 and bootstraps the player
// entirely in TypeScript (no logic in the HTML host). Keeps the live instance for later SendMessage
// bridging from the editor panel.
export class GameView extends Component {
    constructor() {
        super(...arguments);
        this.state = { progress: 0, loading: true, error: null };
        this.canvasRef = createRef();
        this.unityInstance = null;
        this.initialized = false;
        // Stops key events from reaching the Unity player while an HTML input/textarea is focused. The
        // player now releases the page keyboard itself (WebGLKeyboardInput clears captureAllKeyboardInput
        // before the splash screen), so this is the second line of defence: it still covers the deployed
        // builds made before that, and the window before the flag is set. A capture-phase listener on window
        // runs before Unity's document-level handlers, so stopPropagation keeps those keys out of Unity.
        this.keyGuard = (e) => {
            const t = e.target;
            if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) {
                e.stopPropagation();
            }
        };
    }
    async componentDidMount() {
        this.unsubscribeAspect = aspectStore.subscribe(() => this.forceUpdate());
        // Guard against a double mount (e.g. React StrictMode) creating two Unity instances.
        if (this.initialized) {
            return;
        }
        this.initialized = true;
        window.addEventListener("keydown", this.keyGuard, true);
        window.addEventListener("keyup", this.keyGuard, true);
        window.addEventListener("keypress", this.keyGuard, true);
        try {
            await this.loadUnityLoader();
            const canvas = this.canvasRef.current;
            if (!canvas || !window.createUnityInstance) {
                throw new Error("Unity loader is unavailable.");
            }
            const config = {
                dataUrl: `${BUILD_URL}/Conveyor.ContentEditor.data.br`,
                frameworkUrl: `${BUILD_URL}/Conveyor.ContentEditor.framework.js.br`,
                codeUrl: `${BUILD_URL}/Conveyor.ContentEditor.wasm.br`,
                streamingAssetsUrl: "StreamingAssets",
                companyName: "DefaultCompany",
                productName: "Conveyor",
                productVersion: await this.cacheVersion(),
            };
            this.unityInstance = await window.createUnityInstance(canvas, config, (progress) => this.setState({ progress }));
            unityBridge.setInstance(this.unityInstance); // let the editor panel send levels to play
            this.setState({ loading: false });
        }
        catch (e) {
            const message = e instanceof Error ? e.message : "Failed to load the game.";
            this.setState({ error: message, loading: false });
        }
    }
    async componentWillUnmount() {
        this.unsubscribeAspect?.();
        window.removeEventListener("keydown", this.keyGuard, true);
        window.removeEventListener("keyup", this.keyGuard, true);
        window.removeEventListener("keypress", this.keyGuard, true);
        unityBridge.setInstance(null);
        if (this.unityInstance) {
            try {
                await this.unityInstance.Quit();
            }
            catch {
                // ignore teardown errors
            }
            this.unityInstance = null;
        }
    }
    /**
     * The string Unity uses to decide whether its cached copy of data.br is still current.
     *
     * ⚠ Not cosmetic, and it used to be the constant "1.0". Unity's WebGL loader keeps data.br in
     * IndexedDB (`UnityCache`, separate from the HTTP cache) and retains an entry only while
     * `entry.version == config.productVersion` — literally that comparison in the loader's
     * cleanUpCache. A constant tells it every build's data file is the same one, forever, so the one
     * invalidation mechanism Unity provides is switched off.
     *
     * ⚠ It is the ETAG OF data.br, NOT the app's build timestamp, and the difference is 33 MB.
     * The timestamp changes on every image build including a web-only one, which would make every
     * user re-download the whole data file for a deploy that did not touch the player — precisely
     * the cost the two-image build split exists to avoid. The ETag changes when, and only when, the
     * file does: the player arrives in its own artifact image layer, so a web-only rebuild leaves it
     * byte-identical and its ETag alone.
     */
    async cacheVersion() {
        try {
            // HEAD, so this costs headers rather than the file. The loader downloads it moments later
            // either way.
            const response = await fetch(`${BUILD_URL}/Conveyor.ContentEditor.data.br`, { method: "HEAD" });
            const tag = response.headers.get("ETag") ?? response.headers.get("Last-Modified");
            if (response.ok && tag) {
                return tag;
            }
        }
        catch {
            // fall through
        }
        // Deliberately a value that can never match a stored entry, rather than a constant. If the
        // HEAD fails the cost is re-downloading data.br once; the alternative is silently restoring
        // the exact bug this method exists to prevent.
        return `unknown-${Date.now()}`;
    }
    // Injects the Unity loader script exactly once (it registers window.createUnityInstance). Idempotent:
    // resolves immediately if already loaded, and reuses an in-flight <script> instead of adding another.
    loadUnityLoader() {
        if (window.createUnityInstance) {
            return Promise.resolve();
        }
        const existing = document.querySelector("script[data-unity-loader]");
        if (existing) {
            return new Promise((resolve, reject) => {
                existing.addEventListener("load", () => resolve());
                existing.addEventListener("error", () => reject(new Error(`Failed to load ${LOADER_URL}`)));
            });
        }
        return new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = LOADER_URL;
            script.dataset.unityLoader = "true";
            script.onload = () => resolve();
            script.onerror = () => reject(new Error(`Failed to load ${LOADER_URL}`));
            document.body.appendChild(script);
        });
    }
    render() {
        const { progress, loading, error } = this.state;
        return (
        // ⚠ h-full + aspect-ratio, NOT h-screen with a width computed from 100vh.
        //
        // The panel is not the viewport: there is a header above it now. Sizing the player from
        // 100vh made it taller than the space it actually had — the overflow-hidden parent clipped
        // the bottom off, and the width was computed for a height the canvas never got, so the
        // chosen frame no longer matched what was on screen. aspect-ratio derives the width from
        // the height the element REALLY has, so it stays correct whatever sits above or below it.
        // The ratio comes from the Header's Phone/iPad toggle (aspectStore); the editor panel
        // beside this pane is flex-1, so it absorbs the width change.
        _jsxs("div", { className: "relative h-full shrink-0 bg-[#231F20]", style: { aspectRatio: aspectStore.ratio }, children: [_jsx("canvas", { id: CANVAS_ID, ref: this.canvasRef, tabIndex: -1, className: "block h-full w-full" }), loading && !error && (_jsx("div", { className: "absolute left-1/2 top-1/2 w-1/2 -translate-x-1/2 -translate-y-1/2", children: _jsx("div", { className: "h-2 w-full overflow-hidden rounded bg-black/40", children: _jsx("div", { className: "h-full bg-emerald-400 transition-[width]", style: { width: `${Math.round(progress * 100)}%` } }) }) })), error && (_jsx("div", { className: "absolute left-1/2 top-4 -translate-x-1/2 rounded bg-red-600 px-3 py-2 text-sm text-white", children: error }))] }));
    }
}
